import { cn } from "@/lib/cn";

export interface SelectableCardProps {
  selected: boolean;
  onSelect: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  /** Compact horizontal layout (for smaller choices). */
  compact?: boolean;
}

/** A large tappable choice card with a flamingo glow when selected. */
export function SelectableCard({
  selected,
  onSelect,
  title,
  subtitle,
  icon,
  compact = false,
}: SelectableCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "group relative flex rounded-2xl border text-left transition-[border-color,background-color,box-shadow,transform] duration-200",
        compact
          ? "items-center gap-3 p-4"
          : "flex-col items-center gap-3 p-6 text-center",
        selected
          ? "border-flamingo/60 bg-flamingo/[0.08] shadow-glow-sm"
          : "border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]",
      )}
    >
      {icon && (
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-xl transition-colors",
            compact ? "size-10" : "size-14",
            selected
              ? "bg-flamingo/15 text-flamingo"
              : "bg-white/[0.05] text-white/60 group-hover:text-white/80",
          )}
        >
          {icon}
        </span>
      )}
      <span className={compact ? "" : "flex flex-col items-center gap-0.5"}>
        <span
          className={cn(
            "font-display font-semibold",
            compact ? "text-base" : "text-lg",
            selected ? "text-white" : "text-white/85",
          )}
        >
          {title}
        </span>
        {subtitle && (
          <span className="text-xs text-white/55">{subtitle}</span>
        )}
      </span>

      <span
        aria-hidden="true"
        className={cn(
          "absolute right-3 top-3 flex size-5 items-center justify-center rounded-full border transition-all",
          compact && "right-4",
          selected
            ? "border-flamingo bg-flamingo text-white"
            : "border-white/20 text-transparent",
        )}
      >
        <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="m5 12 5 5 9-11" />
        </svg>
      </span>
    </button>
  );
}
